<template>
  <div class="map-container">
  </div>
</template>

<script lang="ts">
import L, { CircleMarkerOptions, LeafletMouseEvent, Map, TileLayerOptions } from "leaflet";
import "leaflet/dist/leaflet.css";
import { notify } from "@kyvg/vue3-notification";
import { defineComponent, PropType } from "vue";

export interface LocationDeg {
  longitudeDeg: number;
  latitudeDeg: number;
}

interface MapOptions extends TileLayerOptions {
  templateUrl: string;
  initialLocation?: LocationDeg;
  initialZoom?: number;
}

interface GeoJSONProp {
  url?: string;
  geojson?: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.GeometryCollection;
  style: Record<string,any>;  // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface Place extends LocationDeg { 
  color?: string;
  fillColor?: string;
  fillOpacity?: number;
  radius?: number;
  name?: string;
}

const defaultMapOptions: MapOptions = {
  templateUrl: 'https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
  minZoom: 1,
  maxZoom: 20,
  subdomains:['mt0','mt1','mt2','mt3'],
  attribution: `&copy <a href="https://www.google.com/maps">Google Maps</a>`,
  className: 'map-tiles'
};

interface CloudData {
  lat: number;
  lon: number;
  cloudCover: number;
}

export default defineComponent({

  emits: ["place", "update:modelValue", "error", "dataclick", "finishLoading"],

  props: {
    showCloudCover: {
      type: Boolean,
      default: false
    },
    detectLocation: {
      type: Boolean,
      default: true
    },
    modelValue: {
      type: Object as PropType<LocationDeg>,
      default() {
        return {
          latitudeDeg: 42.3814,
          longitudeDeg: -71.1281
        };
      }
    },
    mapOptions: {
      type: Object as PropType<MapOptions>,
      default() {
        return defaultMapOptions;
      }
    },
    initialPlace: {
      type: Object as PropType<Place>,
      default: null
    },
    places: {
      type: Array as PropType<Place[]>,
      default() {
        return [];
      }
    },
    placeCircleOptions: {
      type: Object as PropType<Record<string, any>>,  // eslint-disable-line @typescript-eslint/no-explicit-any
      default() {
        return {
          color: "#0000FF",
          fillColor: "#3333FF",
          fillOpacity: 0.5,
          radius: 150 
        };
      }
    },
    placeSelectable: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Boolean,
      default: true
    },
    selectedCircleOptions: {
      type: Object as PropType<Record<string,any>>,  // eslint-disable-line @typescript-eslint/no-explicit-any
      default() {
        return {
          color: "#FF0000",
          fillColor: "#FF0033",
          fillOpacity: 0.5,
          radius: 200
        };
      }
    },
    selectionEvent: {
      type: String as PropType<'click' | 'dblclick'>,
      default: 'click'
    },
    worldRadii: {
      type: Boolean,
      default: false
    },
    
    geoJsonFiles: {
      type: Array as PropType<GeoJSONProp[]>,
      default: () => []
    },
    
    selectedCloudCover: {
      type:  Array as PropType<CloudData[]>,
      default: null
    },
    
    cloudCoverOpacityFunction: {
      type: Function,
      default: (c: number) => c >= 0.05 ? 0.2 + Math.pow(c,1.5) * .8 : c
    },
    
    rectangleDegrees: {
      type: Number,
      default: 1
    }
  },

  mounted() {
    if (this.initialPlace) {
      this.selectedPlace = this.initialPlace;
    }
    if (this.detectLocation) {
      this.getLocation(true);
    }
    this.setup(true);

    // Leaflet caches the container's pixel size at creation time in
    // setup() above, but #guided-content-container's fit-content height
    // (driven by sibling content still laying out) can still be settling
    // at that exact instant -- the map then renders blank until
    // something changes the container's size *again* later (dragging
    // the resize handle, a window resize) for the ResizeObserver below
    // to catch. Force one more measurement after layout has truly
    // settled, independent of whether the size actually changed.
    requestAnimationFrame(() => requestAnimationFrame(() => this.map?.invalidateSize()));

    // We shouldn't need to ever reset this,
    // unlike the regular setup which can get called again
    this.setupResizeObserver();
  },

  data() {
    return {
      resizeObserver: null as ResizeObserver | null,
      eclipsePath: [] as L.GeoJSON[],
      placeCircles: [] as L.CircleMarker[],
      selectedCircle: null as L.CircleMarker | null,
      selectedPlace: null as Place | null,
      selectedPlaceCircle: null as L.CircleMarker | null,
      cloudCoverRectangles: L.layerGroup(),
      map: null as Map | null,
      basemap: null as L.TileLayer | null,
      fromInside: null as boolean | null,
      rectanglesCreated: false,
      index: {} as { [key: string]: number }
    };
  },

  methods: {

    setupResizeObserver() {
      // Observe this component's own root -- the exact node passed to
      // L.map() in setup() -- rather than the app-owned #map-container
      // wrapper it happens to sit inside. Watching the node Leaflet
      // actually measures is correct regardless of how the two are
      // related by CSS.
      this.resizeObserver = new ResizeObserver(() => {
        this.map?.invalidateSize();
      });
      this.resizeObserver.observe(this.$el as HTMLDivElement);
    },
    
    // eslint-disable-next-line @typescript-eslint/naming-convention
    parseResult(result: CloudData[]) {
      if (this.cloudCoverRectangles === null) {
        return;
      }

      // Create an index mapping indices to rectangle layers
      this.index = {};

      result.forEach((row: {'lat': number, 'lon': number, 'cloudCover': number}, index: number) => {
        const lat = row.lat;
        const lon = row.lon;
        const cloudCover = row.cloudCover;

        if (isNaN(lat) || isNaN(lon) || isNaN(cloudCover)) {
          return;
        }

        // Construct index key
        const key = `${lat},${lon}`;

        // Store cloudCover value in index
        this.index[key] = index;

        const rect = this.createRectangle(lat, lon, cloudCover, index);
        if (rect) {
          this.cloudCoverRectangles.addLayer(rect);
        }
      });

      // Add rectangles to the map
      if (this.map !== null) {
        this.cloudCoverRectangles.addTo(this.map as Map);
      } else {
        return;
      }

      this.$emit('finishLoading');
    },

    
    createRectangle(lat: number, lon: number, cloudCover: number, index: number): L.Rectangle {
      const color = this.getColor(cloudCover);
      
      const rect = L.rectangle([
        [lat + this.rectangleDegrees / 2, lon - this.rectangleDegrees / 2],
        [lat - this.rectangleDegrees / 2, lon + this.rectangleDegrees / 2],
      ], {
        stroke: true,
        color: color,
        weight: .01,
        opacity: cloudCover,
        fillColor: color,
        fillOpacity: this.cloudCoverOpacityFunction(cloudCover)
      });
      rect.on('click', () => {
        this.$emit('dataclick', { lat, lon, cloudCover, index});
      });
      return rect;
    },

    getColor(_cloudCover:number) {
      // Calculate HSL color based on a gradient
      const hue = 0;
      const saturation = '0%';
      const lightness = '100%'; // 50% to 100%

      return `hsla(${hue}, ${saturation}, ${lightness},.9)`;
    },    
    
    getLocation(startup=false) {
      const options = { timeout: 10000, enableHighAccuracy: true };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.updateValue({
            longitudeDeg: position.coords.longitude,
            latitudeDeg: position.coords.latitude
          });

          if (this.map) {
            this.map.setView([position.coords.latitude, position.coords.longitude], this.map.getZoom());
          }
        },
        (_error) => {
          const msg = "Unable to autodetect location. Location will default to Cambridge, MA, USA, or you can\nuse the location selector to manually input a location.";
          if (startup) {
            notify({
              group: "startup-location",
              type: "error",
              text: msg,
              duration: 4500
            });
          } else {
            this.$emit("error", msg);
          }
        },
        options
      );
    },
    
    sameLoc(loc1: LocationDeg, loc2: LocationDeg): boolean {
      return loc1.latitudeDeg === loc2.latitudeDeg && loc1.longitudeDeg === loc2.longitudeDeg;
    },

    circleForLocation(location: LocationDeg, circleOptions: Record<string,any>): L.CircleMarker {  // eslint-disable-line @typescript-eslint/no-explicit-any
      return this.circleMaker([location.latitudeDeg, location.longitudeDeg], circleOptions as CircleMarkerOptions);
    },

    circleForSelection() : L.CircleMarker | null {
      if (this.selectedPlace) {
        return null;
      }
      const circle = this.circleForLocation(this.modelValue, { ...this.selectedCircleOptions, interactive: false });
      circle.bringToFront();
      return circle;
    },

    circleForPlace(place: Place): L.CircleMarker {
      const options = (place === this.selectedPlace) ? this.selectedCircleOptions : this.placeCircleOptions;
      const circle = this.circleForLocation(place, options);
      if (place.name) {
        circle.bindTooltip(place.name);
      }
      return circle;
    },

    onPlaceSelect(place: Place) {
      this.fromInside = true;
      this.updateValue({
        longitudeDeg: place.longitudeDeg,
        latitudeDeg: place.latitudeDeg
      });
      this.$emit('place', place);
      this.selectedPlace = place;
    },

    onMapSelect(event: LeafletMouseEvent) {
      this.fromInside = true;
      let longitudeDeg = event.latlng.lng + 180;
      longitudeDeg = ((longitudeDeg % 360) + 360) % 360;  // We want modulo, but JS % operator is remainder
      longitudeDeg -= 180;
      this.selectedPlace = null;
      this.updateValue({
        latitudeDeg: event.latlng.lat,
        longitudeDeg
      });
    },

    setup(initial=false) {
      const mapContainer = this.$el as HTMLDivElement;
      const location: L.LatLngExpression = initial && this.mapOptions.initialLocation ?
        this.locationToLatLng(this.mapOptions.initialLocation) :
        this.latLng;

      const initialZoom = this.mapOptions.initialZoom ?? 4;
      const zoom = initial ? initialZoom : (this.map?.getZoom() ?? initialZoom);
      const map = L.map(mapContainer, {renderer: new L.Canvas()}).setView(location, zoom);

      const options = { ...defaultMapOptions, ...this.mapOptions };
      this.basemap = L.tileLayer(options.templateUrl, options);
      this.basemap.addTo(map);


      this.placeCircles = this.places.map(place => this.circleForPlace(place));
      this.placeCircles.forEach((circle, index) => {
        circle.on('mouseover', () => {
          const place = this.places[index];
          circle.openTooltip([place.latitudeDeg, place.longitudeDeg]);
        });

        if (this.placeSelectable) {
          circle.on('click', () => {
            this.onPlaceSelect(this.places[index]);
          });
        }

        circle.addTo(map);
      });

      this.selectedCircle = this.circleForSelection();
      this.selectedCircle?.addTo(map);

      map.doubleClickZoom.disable();
      if (this.selectable) {
        map.on(this.selectionEvent, this.onMapSelect);
      }

      map.attributionControl.setPrefix('<a href="https://leafletjs.com" title="A JavaScript library for interactive maps" target="_blank" rel="noopener noreferrer" >Leaflet</a>');
      // Moved out of the bottom-right corner so the small map's own
      // bottom-anchored overlay buttons (search, "use my location") can
      // sit flush against the map's edges without overlapping it.
      map.attributionControl.setPosition('topright');
      
      // show the geojson files
      this.geoJsonFiles.forEach((geojsonrecord) => {
        const url = geojsonrecord.url;
        const geo = geojsonrecord.geojson;
        const style = geojsonrecord.style as CircleMarkerOptions;
        if (url) {
          fetch(url)
            .then((response) => response.json())
            .then((data) => {
              const geoJSON = L.geoJSON(data, { style }).addTo(map);
              if (url.includes("center")) {
                geoJSON.bringToFront();
                this.eclipsePath.push(geoJSON);
              }
            })
            .catch((error) => {
              console.error('Error:', error);
            }); 
        } else if (geo) {
          L.geoJSON(geo, {
            style: style,
            pointToLayer: function (feature, latlng) {
              if (feature.properties.absoluteRadius) {
                style.radius = feature.properties.absoluteRadius;
                return L.circle(latlng, style);
              } else {
                return L.circleMarker(latlng, style);
              }
              
            },
            onEachFeature: function (feature, layer) {
              if (feature.properties && feature.properties.popupContent) {
                layer.bindPopup(feature.properties.popupContent);
              }
            }
          }).addTo(map);
        }
      });

      this.eclipsePath.map(g => g.bringToFront());
      this.selectedCircle?.bringToFront();
      
      this.map = map;
      
      this.updateCloudCover(this.showCloudCover);
      this.bringLocationAndPathToFront();
    },

    updateValue(value: LocationDeg) {
      this.$emit('update:modelValue', value);
    },

    updateCircle() {
      if (this.map) {
        this.selectedCircle?.remove();
        this.selectedCircle = this.circleForSelection();
        if (this.selectedCircle) {
          this.selectedCircle.addTo(this.map as Map); // Not sure why, but TS is cranky w/o the Map cast
          this.bringLocationAndPathToFront();
        }
      }
    },

    bringLocationAndPathToFront() {
      this.eclipsePath.map(g => g.bringToFront());
      this.selectedCircle?.bringToFront();
    },

    locationToLatLng(location: LocationDeg): L.LatLngExpression {
      return [location.latitudeDeg, location.longitudeDeg];
    },

    // Public: re-centers/re-zooms the map back to this session's own
    // starting view (mapOptions.initialLocation/initialZoom -- either
    // the app's default map-center-on-the-eclipse-path view, or a
    // shared-link's query-specified location/zoom). Distinct from the
    // selected-location pin resetting to Antiguita, Spain: that's the
    // marker, this is the camera, and they're independent -- the
    // modelValue watcher's own zoom-to-6-on-change wouldn't fire this
    // correctly since it always targets the *pin's* location, not the
    // session's original viewport.
    resetToInitialView() {
      if (!this.map || !this.mapOptions.initialLocation) {
        return;
      }
      this.map.setView(
        this.locationToLatLng(this.mapOptions.initialLocation),
        this.mapOptions.initialZoom ?? 4
      );
    },

    updateRectangleIntensity(val: number | null = null): void {
      (this.cloudCoverRectangles as L.LayerGroup<L.Rectangle>).eachLayer((layer) => {
        if (layer instanceof L.Rectangle) {
          const latLng = layer.getBounds().getCenter();
          const lat = latLng.lat;
          const lon = latLng.lng;
          const key = `${lat},${lon}`;

          const cloudCover = val ?? this.selectedCloudCover[this.index[key]]?.cloudCover;
          if (cloudCover !== undefined) {
            layer.setStyle({fillOpacity: this.cloudCoverOpacityFunction(cloudCover), opacity: cloudCover });
          }
        }
      });
      this.$emit('finishLoading');
    },


    updateCloudCover(value: boolean) {
      if (value) {
        // If rectangles have been created already, update their intensity
        if (this.rectanglesCreated) {
          this.updateRectangleIntensity();
        } else {
          // If rectangles haven't been created yet, run parseResult to create them
          this.parseResult(this.selectedCloudCover);
          this.rectanglesCreated = true; // Set the flag to true
        }
      } else {
        // Set opacity to 0 instead of clearing the layers so the
        // rectangles are ready to show again without recomputing.
        this.updateRectangleIntensity(0);
      }
    },

  },

  computed: {
    circleMaker(): (latlng: L.LatLngExpression, options: L.CircleMarkerOptions) => L.CircleMarker {
      return this.worldRadii ? L.circle : L.circleMarker;
    },
    latLng(): L.LatLngExpression {
      return this.locationToLatLng(this.modelValue);
    }
  },

  watch: {

    selectedCloudCover(val: CloudData[] | null) {
      if (val !== null && val !== undefined) {
        this.updateCloudCover(this.showCloudCover);
        this.bringLocationAndPathToFront();
      }
    },
    
    modelValue(loc: LocationDeg, oldLoc: LocationDeg) {
      this.updateCircle();
      const needZoom = !this.fromInside && !this.sameLoc(loc, oldLoc);
      if (this.map && (!this.map.getBounds().contains(this.latLng) || needZoom)) {
        this.map.setView(this.latLng, needZoom ? 6 : this.map.getZoom());
      }
      this.fromInside = false;
    },
    
    mapOptions(newOptions: MapOptions, oldOptions: MapOptions) {
      if (oldOptions === null || newOptions === null) {
        return;
      }
      if (newOptions.templateUrl !== oldOptions.templateUrl) {
        this.basemap?.setUrl(newOptions.templateUrl ?? defaultMapOptions.templateUrl);
      }
    },
    
    
    showCloudCover(value: boolean) {
      this.updateCloudCover(value);
      this.bringLocationAndPathToFront();
    },
    places() {
      this.map?.remove();
      this.setup();
    },
    selectedPlace(newPlace) {
      const index = this.places.indexOf(newPlace);
      const oldSelectedCircle = this.selectedPlaceCircle;
      this.selectedPlaceCircle = this.placeCircles[index];

      oldSelectedCircle?.setStyle(this.placeCircleOptions);
      this.selectedPlaceCircle?.setStyle(this.selectedCircleOptions);
    }
  }
  
});
</script>

<style lang="less">
.map-container {
  // height: 100% (or relying on the parent's align-items: stretch)
  // proved unreliable here in practice -- the parent (#map-container in
  // SolarEclipse2026.vue) itself gets its own height from a flex-grow
  // chain rather than an explicitly authored height, and empirically
  // this element still collapsed to near-zero height even with
  // align-items: stretch set on the parent and no explicit height set
  // here. Absolute positioning against the parent's already-final,
  // concretely computed box size sidesteps that chain entirely -- the
  // parent (#map-container) has position: relative already.
  position: absolute;
  inset: 0;
  width: auto;
  margin: auto;
  padding: 0;
  border-radius: 5px;

  .leaflet-control-container {
    height: 100%;
    position: relative;
  }
  
  .leaflet-top.leaflet-right::before {
    content: " Credit: © Leaflet.js";
    // Anchors the label's top-right corner flush against the control
    // container's own top-right corner (the container itself already
    // sits at the map's top-right, moved there via
    // map.attributionControl.setPosition('topright')), growing
    // downward-left -- the mirror image of the old bottom-right anchor,
    // which grew upward-left from the container's bottom-right corner.
    top: 0;
    left: 100%;
    transform: translate(-100%, 0);
    pointer-events: auto;
    color: #0078a8;
    background-color: rgba(255,255,255,0.8);
    font-size: 0.75em;
    padding-inline: 0.5em;
    padding-block: 0.3em;
  }

  .leaflet-top.leaflet-right:hover::before {
    content: "";
    background-color: transparent;
  }

  .leaflet-top.leaflet-right:hover > .leaflet-control-attribution {
    display: block;
  }


  .leaflet-control-attribution {
    display: none;
  }

  // @cosmicds/vue-toolkit's bundled vendor chunk carries its own stale,
  // internal copy of this exact CSS (an older revision of this file,
  // also rooted at .map-container -- a generic enough class name that
  // it collides directly with ours). Since the toolkit ships as one
  // eager UMD bundle, importing ANY of its components for unrelated
  // reasons (icon-button, geolocation-button, etc.) evaluates that
  // whole bundle and injects its <style> tags regardless -- including
  // its old .map-container .leaflet-bottom.leaflet-right::before rule,
  // which still renders "Credit: © Leaflet.js" in the map's bottom-right
  // corner even though our own copy of this rule moved to top-right.
  // Can't fix the vendored copy (it's in node_modules), so explicitly
  // neutralize it here instead.
  .leaflet-bottom.leaflet-right::before {
    content: none !important;
  }

  path.leaflet-interactive:focus {
    outline: none;
  }

  path.leaflet-interactive:focus-visible {
    outline: auto 5px black;
  }

  
}
</style>
